import { type SurveyUserV2 } from '@capsicum/common';
import {
	ANSWER_STATUS_DISABLED,
	ANSWER_STATUS_SELECTED,
	ANSWER_STATUS_UNSELECTED,
	ANSWER_STATUS_VOTE_PENDING,
	ANSWER_STATUS_VOTED,
	type AnswerStatusType,
	MOBILE_TYPE_IMAGE,
	MOBILE_TYPE_VIDEO,
	MODAL_TYPE_IMAGE,
	MODAL_TYPE_VIDEO,
	type ModalTypes,
	selectedAnswerAtom,
	type SurveyMedia,
	type UseSurveyAnswerGroupProps
} from '@capsicum/features/survey/shared';
import { useAtom } from 'jotai';
import { useEffect } from 'react';

type SurveyAnswer = NonNullable<SurveyUserV2['answers']>[number];

export const useSurveyAnswerGroup = ({
	hasUserVoted,
	isSurveyExpired,
	isUserVotePendingBlockchain,
	onAnswerChange,
	userCanVote
}: UseSurveyAnswerGroupProps) => {
	const [selectedAnswer, setSelectedAnswer] = useAtom(selectedAnswerAtom);

	useEffect(() => {
		if (selectedAnswer) {
			onAnswerChange(selectedAnswer);
		}

		return () => onAnswerChange('');
	}, [onAnswerChange, selectedAnswer, setSelectedAnswer]);

	const onAnswerPress = (answerId: string) => {
		setSelectedAnswer(answerId);
	};

	const getAnswerImages = (answer: SurveyAnswer) =>
		answer.images
			? answer.images
					.filter(image => image)
					.map(image => {
						return {
							mediaType: image?.imageType?.type === MOBILE_TYPE_VIDEO ? 'video' : 'image',
							// TODO: Replace url with thumbnailUrl when BE implement it
							thumbnailUrl: image.url || '',
							url: image.url || ''
						} satisfies SurveyMedia;
					})
			: undefined;

	const getAnswerKey = (answer: SurveyAnswer, index: number) => answer.id || `${index}`;

	const getAnswerThumbnail = (answer: SurveyAnswer) =>
		answer.images?.find(item => item?.imageType?.type === MOBILE_TYPE_IMAGE)?.url ||
		answer.images?.find(item => item)?.url;

	const getAnswerText = (answer: SurveyAnswer) => answer.answer;

	const getAnswerStatus: (answer: SurveyAnswer) => AnswerStatusType = (answer: SurveyAnswer) => {
		if (answer.hasUserVotedFor) {
			return ANSWER_STATUS_VOTED;
		}

		if (!userCanVote || (selectedAnswer !== answer.id && isUserVotePendingBlockchain)) {
			return ANSWER_STATUS_DISABLED;
		}

		if (selectedAnswer === answer.id && isUserVotePendingBlockchain) {
			return ANSWER_STATUS_VOTE_PENDING;
		}

		if (selectedAnswer === answer.id) {
			return ANSWER_STATUS_SELECTED;
		}

		return ANSWER_STATUS_UNSELECTED;
	};

	const getModalType = (answer: SurveyAnswer): ModalTypes => {
		const answerImages = answer.images;

		const answerContainsVideo = answerImages?.find(image => image.imageType?.type === MOBILE_TYPE_VIDEO);
		const answerContainsMultipleImages = answerImages && answerImages.length > 1;

		if (answerContainsVideo) {
			return MODAL_TYPE_VIDEO;
		}

		if (answerContainsMultipleImages) {
			return MODAL_TYPE_IMAGE;
		}

		return undefined;
	};

	const getShowRadioButton = (answer: SurveyAnswer) => {
		const surveyAnswerStatus = getAnswerStatus(answer);

		if (
			surveyAnswerStatus === ANSWER_STATUS_VOTED ||
			hasUserVoted ||
			isSurveyExpired ||
			isUserVotePendingBlockchain
		) {
			return false;
		}

		return true;
	};

	const getAnswerFormattedData = (answer: SurveyAnswer, index: number) => {
		return {
			hasModalFor: getModalType(answer),
			key: getAnswerKey(answer, index),
			mediaItems: getAnswerImages(answer),
			showRadioButton: getShowRadioButton(answer),
			status: getAnswerStatus(answer),
			text: getAnswerText(answer),
			thumbnailUrl: getAnswerThumbnail(answer)
		};
	};

	return {
		getAnswerFormattedData,
		onAnswerPress
	};
};
